/**
 * Arquivos guardados no servidor (o Desk Cloud do RPG Desk): link assinado pedido na
 * hora e guardado por um tempo, e o envio, com as medidas, o tipo e a miniatura
 * (docs/spec/desk-cloud.md).
 */
import { useEffect, useState } from 'react';
import { api, type UploadOptions } from './api.js';
import { inferKind, type DeskFile, type KindOrigin } from './shared.js';
import { useApp } from './store.js';
import { translate, useLanguage } from './i18n/index.js';
import { uploadRefusal } from './plans/planModel.js';
import { attachThumb, loadImage, toBlob } from './cloud/thumbs.js';
import { formatBytes } from './bytes.js';

const say = (key: string, params?: Record<string, string | number>) => translate(useLanguage.getState().language, key, params);

const cache = new Map<string, { url: string; at: number }>();
const TTL = 30 * 60_000;

/** Esquece os links guardados: eram da conta que saiu (o do dono abre até a lixeira). */
export function forgetFileUrls(): void {
  cache.clear();
}

/** Link do arquivo (null enquanto carrega; 'error' se sumiu ou não é acessível). */
export function useFileUrl(fileId: string | null): string | null | 'error' {
  const token = useApp((s) => s.token);
  const fresh = (id: string | null) => {
    const hit = id ? cache.get(id) : undefined;
    return hit && Date.now() - hit.at < TTL ? hit.url : null;
  };
  const [state, setState] = useState<{ id: string | null; url: string | null | 'error' }>({ id: fileId, url: fresh(fileId) });
  const url = state.id === fileId ? state.url : fresh(fileId);
  useEffect(() => {
    if (!fileId || !token) return;
    const hit = fresh(fileId);
    if (hit) {
      setState({ id: fileId, url: hit });
      return;
    }
    let alive = true;
    api
      .fileUrl(token, fileId)
      .then(({ url: link }) => {
        cache.set(fileId, { url: link, at: Date.now() });
        if (alive) setState({ id: fileId, url: link });
      })
      .catch(() => alive && setState({ id: fileId, url: 'error' }));
    return () => {
      alive = false;
    };
  }, [fileId, token]);
  return fileId ? url : null;
}

/**
 * Envia um arquivo ligado à sala aberta (quem frequenta a sala consegue o link). `origin` é o lugar do
 * envio, que decide o tipo antes do mime: soltar na cena é token, o fundo é mapa, a música é áudio.
 */
export async function uploadRoomFile(file: File, origin?: KindOrigin): Promise<DeskFile> {
  const state = useApp.getState();
  // avisa antes de enviar o que o servidor recusaria (docs/spec/planos.md)
  const refusal = uploadRefusal(state, file.size, say);
  if (refusal) throw new Error(refusal);
  return sendToCloud(file, { roomId: state.activeRoomId, ...(origin ? { origin } : {}) });
}

/** Envia ao meu Desk Cloud sem ligar a mesa nenhuma (a capa do perfil não é da mesa aberta). */
export async function uploadMyFile(file: File): Promise<DeskFile> {
  const refusal = uploadRefusal(useApp.getState(), file.size, say);
  if (refusal) throw new Error(refusal);
  return sendToCloud(file);
}

/**
 * Envia ao Desk Cloud com o que o navegador sabe do arquivo: as medidas, o tipo (o lugar do envio,
 * `origin`, decide antes) e, nas imagens, a miniatura, depois do envio e sem segurar quem chamou.
 */
export async function sendToCloud(file: File, options: Omit<UploadOptions, 'kind' | 'width' | 'height' | 'durationMs'> & { origin?: KindOrigin } = {}): Promise<DeskFile> {
  const { token } = useApp.getState();
  if (!token) throw new Error(say('error.noSession'));
  const { origin, ...upload } = options;
  const measures = await measureFile(file);
  const kind = inferKind({ mime: file.type, name: file.name, width: measures.width ?? null, height: measures.height ?? null }, origin);
  const { file: stored } = await api.uploadFile(token, file, { ...upload, ...measures, kind });
  void attachThumb(stored, file);
  return stored;
}

/** As medidas que o navegador sabe tirar: a largura e a altura da imagem, a duração do áudio. */
export async function measureFile(file: File): Promise<{ width?: number; height?: number; durationMs?: number }> {
  try {
    if (file.type.startsWith('image/')) return await imageSize(file);
    if (file.type.startsWith('audio/')) return { durationMs: await audioDuration(file) };
  } catch {
    // sem as medidas, o tipo sai do mime
  }
  return {};
}

/** A duração de um áudio local, em ms; falha se o navegador não ler em 5 s. */
function audioDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    const done = (fn: () => void) => {
      window.clearTimeout(timer);
      URL.revokeObjectURL(url);
      audio.removeAttribute('src');
      fn();
    };
    const timer = window.setTimeout(() => done(() => reject(new Error('sem duração'))), 5000);
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => done(() => (Number.isFinite(audio.duration) ? resolve(audio.duration * 1000) : reject(new Error('sem duração'))));
    audio.onerror = () => done(() => reject(new Error('sem duração')));
    audio.src = url;
  });
}

/** Tamanho natural de uma imagem local. */
export function imageSize(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      reject(new Error(say('error.badImage')));
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

/** O que uma imagem precisa respeitar para ir ao Desk Cloud: os tipos, o maior arquivo e o maior lado. */
export type ImageRules = { types: readonly string[]; maxBytes: number; maxSide: number };

/**
 * A imagem dentro das regras (a capa e a marca da mesa). Vai como está se o tipo, o tamanho e o maior
 * lado já cabem; um GIF que cabe no tamanho também, para não perder a animação. Senão, é reduzida num
 * canvas para WebP (JPEG onde o navegador não grava WebP), com o mesmo nome. Falha se nem assim couber.
 */
export async function fitImage(file: File, rules: ImageRules): Promise<File> {
  if (rules.types.includes(file.type) && file.size <= rules.maxBytes) {
    if (file.type === 'image/gif') return file;
    const { width, height } = await imageSize(file);
    if (Math.max(width, height) <= rules.maxSide) return file;
  }
  let img: HTMLImageElement;
  try {
    img = await loadImage(file);
  } catch {
    throw new Error(say('error.badImage'));
  }
  const scale = Math.min(1, rules.maxSide / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
  let blob = await toBlob(canvas, 'image/webp', 0.85);
  if (!blob || blob.type !== 'image/webp') blob = await toBlob(canvas, 'image/jpeg', 0.85);
  if (!blob || blob.size > rules.maxBytes || !rules.types.includes(blob.type)) throw new Error(say('imagePicker.tooBig', { max: formatBytes(rules.maxBytes) }));
  const base = file.name.replace(/\.[^.]+$/, '') || 'imagem';
  return new File([blob], `${base}.${blob.type === 'image/webp' ? 'webp' : 'jpg'}`, { type: blob.type });
}

/** Link de um arquivo do RPG Desk, fora de componentes (usa o mesmo cache de `useFileUrl`). */
export async function fileUrl(fileId: string): Promise<string> {
  const hit = cache.get(fileId);
  if (hit && Date.now() - hit.at < TTL) return hit.url;
  const { token } = useApp.getState();
  if (!token) throw new Error(say('error.noSession'));
  const { url } = await api.fileUrl(token, fileId);
  cache.set(fileId, { url, at: Date.now() });
  return url;
}

/** Tamanho natural da imagem num endereço; falha se não carregar em `timeoutMs`. */
export function imageSizeAt(url: string, timeoutMs = 20_000): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const timer = window.setTimeout(() => {
      img.src = '';
      reject(new Error(say('error.imageSlow')));
    }, timeoutMs);
    img.onload = () => {
      window.clearTimeout(timer);
      if (img.naturalWidth > 0 && img.naturalHeight > 0) resolve({ width: img.naturalWidth, height: img.naturalHeight });
      else reject(new Error(say('error.badImage')));
    };
    img.onerror = () => {
      window.clearTimeout(timer);
      reject(new Error(say('error.imageLoad')));
    };
    img.src = url;
  });
}
